import { useMock, corporateGet, corporatePost, corporatePut, corporatePatch, corporateDelete } from './client';
import organizationalUnitsMock from '@/mocks/corporate/organizationalUnits';

function normalizeStatus(status) {
    if (status === 'active' || status === 'inactive') return status;
    if (status === 1 || status === '1' || status === true) return 'active';
    if (status === 0 || status === '0' || status === false) return 'inactive';
    return status ? 'active' : 'inactive';
}

export function normalizeOrganizationalUnit(unit = {}) {
    if (!unit || typeof unit !== 'object') return unit;
    return {
        ...unit,
        id: unit.id,
        name: unit.name || '',
        description: unit.description || '',
        type: unit.type || 'sector',
        status: normalizeStatus(unit.status),
        parentId: unit.parentId || null,
        managerUserId: unit.managerUserId || unit.managerId || null,
        managerName: unit.managerName || unit.manager?.name || '',
    };
}

function normalizeListResponse(res = {}) {
    const content = Array.isArray(res.content)
        ? res.content.map(normalizeOrganizationalUnit)
        : [];
    return {
        ...res,
        content,
        managers: Array.isArray(res.managers) ? res.managers : [],
        totalElements: Number(res.totalElements ?? content.length) || 0,
        page: Number(res.page ?? 0) || 0,
        size: Number(res.size ?? content.length) || 0,
    };
}

export default {
    /**
     * GET /corporate/organizational-units
     * Para setores no create/edit: { type: 'sector', status: 'active' }
     */
    async list(accessToken, params = {}) {
        if (useMock()) {
            const res = await organizationalUnitsMock.list(params);
            return normalizeListResponse(res);
        }
        const res = await corporateGet('/corporate/organizational-units', accessToken, {
            type: params.type || undefined,
            status: params.status || undefined,
            parentId: params.parentId || undefined,
            search: params.search || undefined,
            page: params.page != null ? params.page : undefined,
            size: params.size != null ? params.size : undefined,
        });
        return normalizeListResponse(res);
    },

    /**
     * Atalho: lista setores para selects (create/edit colaborador).
     * Por padrão não força status — alguns backends rejeitam o filtro.
     */
    async listSectors(accessToken, params = {}) {
        return this.list(accessToken, {
            type: 'sector',
            status: params.status || undefined,
            search: params.search || undefined,
        });
    },

    async tree(accessToken) {
        if (useMock()) return organizationalUnitsMock.tree();
        return corporateGet('/corporate/organizational-units/tree', accessToken);
    },

    async getById(accessToken, id) {
        if (useMock()) return normalizeOrganizationalUnit(await organizationalUnitsMock.getById(id));
        return normalizeOrganizationalUnit(
            await corporateGet(`/corporate/organizational-units/${id}`, accessToken),
        );
    },

    async getStats(accessToken, id) {
        if (useMock()) {
            const unit = await organizationalUnitsMock.getById(id);
            return {
                id: unit?.id,
                type: unit?.type || 'sector',
                name: unit?.name,
                status: unit?.status || 'active',
                collaboratorsCount: 0,
                rolesCount: 0,
                coursesCount: 0,
                completionAvg: 0,
                overdueTotal: 0,
                courses: [],
                roles: [],
                courseIds: [],
            };
        }
        return corporateGet(`/corporate/organizational-units/${id}/stats`, accessToken);
    },

    async create(accessToken, payload) {
        if (useMock()) return organizationalUnitsMock.create(payload);
        return corporatePost('/corporate/organizational-units', payload, accessToken);
    },

    async update(accessToken, id, payload) {
        if (useMock()) return organizationalUnitsMock.update(id, payload);
        return corporatePut(`/corporate/organizational-units/${id}`, payload, accessToken);
    },

    async remove(accessToken, id) {
        if (useMock()) return organizationalUnitsMock.remove(id);
        return corporateDelete(`/corporate/organizational-units/${id}`, accessToken);
    },

    async updateStatus(accessToken, id, status) {
        if (useMock()) return organizationalUnitsMock.updateStatus(id, status);
        return corporatePatch(`/corporate/organizational-units/${id}/status`, { status }, accessToken);
    },

    async move(accessToken, id, payload) {
        if (useMock()) return organizationalUnitsMock.move(id, payload);
        return corporatePatch(`/corporate/organizational-units/${id}/move`, payload, accessToken);
    },
};
